import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import type { AttendanceBoardRow, AuthorizedPickupResponse } from '../api/types';
import { useApi } from '../auth/ApiProvider';
import { useI18n } from '../i18n/I18nProvider';
import { Input, Select } from './Field';
import { Button, Card, Notice, Row, Stack } from './primitives';
import { ErrorState } from './states';

/**
 * Check-out, with who may collect the child and who may not (FR-ATT-05, Q30).
 *
 * Releasing a child to the wrong adult is the failure with the worst consequence and no remedy afterwards,
 * so the collectors are presented rather than typed: the child's live guardians and the pickups authorised
 * today. Whoever the child is never released to is shown above them, in red, because the new member of staff is
 * the one who does not know. Departing from the list takes a written reason, which reaches the primary guardian
 * immediately, and, where there is anyone on the red list, saying it was read.
 */
export function CheckOutDialog({ row, onClose, onDone }: {
  row: AttendanceBoardRow; onClose: () => void; onDone: () => void;
}) {
  const api = useApi();
  const { t } = useI18n();
  // 'g:<link>' for a guardian, 'p:<pickup>' for an authorised pickup.
  const [choice, setChoice] = useState<string>('');
  const [personName, setPersonName] = useState('');
  const [reason, setReason] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);

  const collectors = useQuery({
    queryKey: ['collectors', row.childId],
    queryFn: () => api.pickups.collectors(row.childId),
  });

  const overriding = choice === 'other';
  const doNotRelease = collectors.data?.doNotRelease ?? [];

  const checkOut = useMutation({
    mutationFn: () => api.attendance.checkOut({
      childId: row.childId,
      ...(choice.startsWith('g:') ? { guardianLinkId: Number(choice.slice(2)) }
        : choice.startsWith('p:') ? { authorizedPickupId: Number(choice.slice(2)) }
        : {
          overridePersonName: personName || null,
          overrideReason: reason || null,
          acknowledgedRestrictions: acknowledged,
        }),
    }),
    onSuccess: onDone,
  });

  // Nothing chosen is not a check-out. Releasing a child with no record of who collected them is the
  // failure this screen exists to prevent, and it must not be reachable by leaving the list alone
  // (FR-ATT-05).
  const blocked = choice === ''
    || (overriding && (!personName.trim() || !reason.trim() || (doNotRelease.length > 0 && !acknowledged)));

  return (
    <Card title={t('attendance.choosePickup')} hint={row.preferredName ?? row.firstName}>
      <Stack>
        {collectors.isError && <ErrorState error={collectors.error} />}
        {checkOut.isError && <ErrorState error={checkOut.error} />}

        {doNotRelease.length > 0 && (
          <Notice tone="danger" title={t('attendance.doNotRelease')}>
            <Stack tight>
              {doNotRelease.map((entry) => (
                <span key={`${entry.restrictionId ?? 'g'}-${entry.guardianLinkId ?? 'r'}`}>
                  <strong>{entry.fullName}</strong>
                  {' — '}
                  {entry.note ?? t('attendance.doNotRelease.suspended')}
                </span>
              ))}
            </Stack>
          </Notice>
        )}

        <Select
          aria-label={t('attendance.choosePickup')}
          value={choice}
          onChange={(event) => setChoice(event.target.value)}
        >
          <option value="">—</option>
          {(collectors.data?.guardians.length ?? 0) > 0 && (
            <optgroup label={t('attendance.guardians')}>
              {collectors.data!.guardians.map((guardian) => (
                <option key={guardian.guardianLinkId} value={`g:${guardian.guardianLinkId}`}>
                  {`${guardian.fullName ?? '—'} · ${t(`guardians.relationship.${guardian.relationship}`)}`}
                </option>
              ))}
            </optgroup>
          )}
          {(collectors.data?.pickups.length ?? 0) > 0 && (
            <optgroup label={t('attendance.pickups')}>
              {collectors.data!.pickups.map((pickup) => (
                <option key={pickup.id} value={`p:${pickup.id}`}>{pickupLabel(pickup, t)}</option>
              ))}
            </optgroup>
          )}
          <option value="other">{t('attendance.pickupOther')}</option>
        </Select>

        {overriding && (
          <>
            <Notice tone="warning" title={t('attendance.pickupOverride')}>
              {t('attendance.pickupReason')}
            </Notice>
            <Input
              value={personName}
              onChange={(event) => setPersonName(event.target.value)}
              placeholder={t('attendance.pickupName')}
              aria-label={t('attendance.pickupName')}
            />
            <Input
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder={t('attendance.pickupReason')}
              aria-label={t('attendance.pickupReason')}
            />
            {doNotRelease.length > 0 && (
              <label className="dg-checkbox">
                <input type="checkbox" checked={acknowledged} onChange={(event) => setAcknowledged(event.target.checked)} />
                <span>{t('attendance.doNotRelease.acknowledge')}</span>
              </label>
            )}
          </>
        )}

        <Row>
          <Button
            busy={checkOut.isPending}
            busyLabel={t('action.saving')}
            disabled={blocked}
            onClick={() => checkOut.mutate()}
          >
            {t('attendance.checkOut')}
          </Button>
          <Button variant="ghost" onClick={onClose}>{t('action.cancel')}</Button>
        </Row>
      </Stack>
    </Card>
  );
}

function pickupLabel(pickup: AuthorizedPickupResponse, t: (key: string) => string): string {
  const relationship = pickup.relationship ? ` · ${pickup.relationship}` : '';
  const expired = pickup.isValidToday ? '' : ` · ${t('pickups.expired')}`;
  return `${pickup.fullName}${relationship}${expired}`;
}
